import * as React from 'react';

import {colors} from 'shells/colors/colors';
import {InvoiceIcon} from 'shells/icon/svg/invoice-icon';
import {QuoteIcon} from 'shells/icon/svg/quote-icon';

import type {SalesDocumentVariant} from 'nutshell-graphql-types';
import type {WidgetFragment as Widget} from 'nutshell-graphql-types';

import {CardWidgetWrapper} from '../../../core/card/card-widget-wrapper';
import {CollapsibleCardWidget} from '../../../core/card/collapsible-card-widget';
import {getWidgetEmptyStateProps} from '../../../core/card/helpers';

import {useGetInvoices} from '../../sales-documents/invoices/graphql/hooks';
import {useGetQuotes} from '../../sales-documents/quotes/graphql/hooks';
import {handleSalesDocumentChange} from '../../sales-documents/shared';
import {useCreateSalesDocument} from '../../sales-documents/shared/graphql/hooks';

import {SidebarSalesDocumentsSection} from './sidebar-sales-documents-section';

type Props = {
    leadId: string;
    widget?: Widget;
};

export function SalesDocumentsWidgetWrapper(props: Props) {
    const [error, setError] = React.useState<string | null | undefined>(null);

    const refetchLoading = React.useRef<boolean>(false);

    const {
        isFirstLoad: isFirstLoadQuotes,
        quotes,
        refetch: refetchQuotes,
    } = useGetQuotes(props.leadId);
    const {
        isFirstLoad: isFirstLoadInvoices,
        invoices,
        refetch: refetchInvoices,
    } = useGetInvoices(props.leadId);
    const {createSalesDocument} = useCreateSalesDocument();

    const handleCreateSalesDocument = (salesDocumentVariant: SalesDocumentVariant) => {
        createSalesDocument({
            leadId: props.leadId,
            salesDocumentVariant: salesDocumentVariant,
        })
            .then((response) => {
                const salesDocumentRes =
                    response && response.data && response.data.salesDocumentCreate;
                if (salesDocumentRes) {
                    handleSalesDocumentChange(
                        salesDocumentRes.salesDocument.modalUrl,
                        refetchLoading,
                        salesDocumentVariant === 'QUOTE' ? refetchQuotes : refetchInvoices
                    );
                }
            })
            .catch(() => {
                setError(
                    `Error creating ${salesDocumentVariant.toLowerCase()}, please refresh the page and try again.`
                );
            });
    };

    const selectMenuOptions = [
        {
            // @ts-expect-error
            icon: <QuoteIcon size={16} fill={colors.greyDk} wrapWithDiv={false} />,
            label: 'Add a quote',
            value: 'quote',
            handleSelect: () => handleCreateSalesDocument('QUOTE'),
        },
        {
            icon: <InvoiceIcon size={16} wrapWithDiv={false} />,
            label: 'Add an invoice',
            value: 'invoice',
            handleSelect: () => handleCreateSalesDocument('INVOICE'),
        },
    ];

    const count = quotes.length + invoices.length;
    const isEmpty = count === 0;

    const content = (
        <SidebarSalesDocumentsSection
            leadId={props.leadId}
            refetchLoadingRef={refetchLoading}
            quotes={quotes}
            invoices={invoices}
            refetchQuotes={refetchQuotes}
            refetchInvoices={refetchInvoices}
        />
    );

    return (
        <>
            {NutClientConfig.hasLeadPageRedesignM3FF ? (
                props.widget && (
                    <CollapsibleCardWidget
                        widget={props.widget}
                        addButtonProps={{
                            variant: 'multiple',
                            selectMenuOptions: selectMenuOptions,
                        }}
                        errorProps={{
                            error: error,
                            setError: setError,
                        }}
                        emptyStateProps={getWidgetEmptyStateProps('leadSalesDocuments', isEmpty)}
                        isLoading={isFirstLoadQuotes || isFirstLoadInvoices}
                        count={count}
                    >
                        {content}
                    </CollapsibleCardWidget>
                )
            ) : (
                <CardWidgetWrapper
                    widgetKey='leadSalesDocuments'
                    count={count}
                    addButtonProps={{
                        variant: 'multiple',
                        selectMenuOptions: selectMenuOptions,
                    }}
                    errorProps={{
                        error: error,
                        setError: setError,
                    }}
                    isLoading={isFirstLoadQuotes || isFirstLoadInvoices}
                    isEmpty={isEmpty}
                >
                    {content}
                </CardWidgetWrapper>
            )}
        </>
    );
}
